/**
 * plantillaRepse.js
 *
 * Bloques literales de la plantilla oficial de "Subcontratación de servicios
 * especializados" (REPSE / ICSOE). Vive aparte de ReporteRepse.js porque es
 * contenido copiado de la plantilla que entrega el cliente, no lógica: si la
 * autoridad publica una versión nueva, se cambia aquí y nada más.
 *
 * Estructura de cada hoja, celda por celda (el generado debe empatar con
 * "Plantilla REPSE 2025 JUN.xlsx"):
 *
 *   Hoja "Subcontratacion de servicios es"
 *     fila 1-2  banners, con merges B1:D2, E1:H1, F2:G2
 *     fila 3    encabezados (17 columnas)
 *     fila 4    "CAMPO OBLIGATORIO"
 *     fila 5    "ESPECIFICACIONES PARA EL LLENADO" + la guía de cada columna
 *     fila 6+   datos, y al cierre el renglón de Totales
 *     al pie    2 notas sueltas
 *
 *   Hoja "Datos Trabajadores Subcontra"
 *     fila 1    banners, con merges A1:B1 y N1:T1
 *     fila 2    encabezados (20 columnas)
 *     fila 3    "CAMPO OBLIGATORIO"
 *     fila 4    "ESPECIFICACIONES PARA EL LLENADO" + la guía de cada columna
 *     fila 5+   datos, y al cierre el renglón de Totales
 *
 * NOTA sobre las especificaciones: en la plantilla original esos textos traen
 * decenas de espacios seguidos, usados como saltos de línea a mano dentro de la
 * celda. Aquí van con un solo espacio y la celda con wrapText, que se lee igual
 * en Excel; el texto es palabra por palabra el mismo.
 */

// ─── Hoja 1: Subcontratación de servicios especializados ─────────────────────

export const NOMBRE_HOJA_CONTRATOS = "Subcontratacion de servicios es";

export const HEADERS_CONTRATOS = [
  "Consecutivo",
  "Nombre, denominación o razón social",
  "RFC",
  "RP",
  "Objeto del contrato",
  "Inicio del contrato",
  "Conclusión del contrato",
  "No. de trabajadores",
  "¿El personal de servicios especializados proporcionado al beneficiario, realizó los trabajos con elementos propios del prestador de servicios?",
  "¿El beneficiario del personal de servicios especializados, es responsable de la dirección, supervisión o capacitación del personal proporcionado?",
  "Folio del registro REPSE",
  "Fecha del registro REPSE",
  "Estatus del registro REPSE",
  "Actividad económica especializada manifestada en REPSE",
  "Folio del contrato reportado en ICSOE",
  "Folio ICSOE",
  "Fecha de presentación ICSOE",
];

export const ESPECIFICACIONES_CONTRATOS = [
  "ESPECIFICACIONES PARA EL LLENADO",
  "**Nombre de la empresa de la empresa prestadora de servicios",
  "**RFC de la empresa de la empresa prestadora de servicios",
  "**RP de la empresa de la empresa prestadora de servicios",
  "** De acuerdo al contrato",
  "**Formato de fecha DD/MM/AAAA",
  "**Formato de fecha DD/MM/AAAA",
  "** El N° de trabajadores debe coincidir con el que se encuentra en el Acuse de ICSOE",
  "** Opciones de respuesta: SI O NO",
  "** Opciones de respuesta: SI O NO",
  "**Es el folio que vienen en el acuse de Aviso de Registro REPSE Ejemplo: AR99990/2021",
  "**Formato de fecha DD/MM/AAAA",
  "**Opciones de respuesta: Registro en autorización, Registro vigente, Registro en actualización, Sin registro, Registro cancelado",
  "**Se encuentra registrada en REPSE en el Aviso de Registro REPSE",
  "** Ejemplo A21P0000001",
  "** Ejemplo N21P0000001 Nota: pueden ser tambien las declaraciones complementarias",
  "**Formato de fecha DD/MM/AAAA",
];

// Banners de las dos primeras filas. `columna` es el índice 0-based donde va el
// texto; el resto de la fila queda vacía y se cubre con los merges.
export const BANNERS_CONTRATOS = [
  { fila: 0, columna: 1, texto: "Datos del prestador de servicios de personal" },
  { fila: 0, columna: 4, texto: "Datos del contrato" },
  { fila: 1, columna: 5, texto: "Vigencia" },
];

export const MERGES_BANNER_CONTRATOS = [
  { s: { r: 0, c: 1 }, e: { r: 1, c: 3 } }, // B1:D2
  { s: { r: 0, c: 4 }, e: { r: 0, c: 7 } }, // E1:H1
  { s: { r: 1, c: 5 }, e: { r: 1, c: 6 } }, // F2:G2
];

// Notas sueltas del pie de la hoja original (columnas E y F).
export const NOTAS_CONTRATOS = [
  { columna: 4, texto: "Cuenta con contrado de prestacion de servicios" },
  { columna: 5, texto: "en el contrato se especifica como condicion el contar con registro repse" },
];

export const ANCHOS_CONTRATOS = [
  12, 38, 16, 16, 34, 15, 16, 14, 34, 34, 18, 16, 20, 30, 20, 18, 18,
];

// ─── Hoja 2: Datos de los trabajadores subcontratados ────────────────────────

export const NOMBRE_HOJA_TRABAJADORES = "Datos Trabajadores Subcontra";

export const HEADERS_TRABAJADORES = [
  "Consecutivo",
  "RP del prestador de servicios especializados",
  "Primer apellido",
  "Segundo apellido",
  "Nombre(s)",
  "NSS",
  "RFC",
  "CURP",
  "Puesto desempeñado",
  "Tipo de personal",
  "SBC",
  "Fecha de ingreso",
  "Días trabajados o pagados",
  "Folio del registro REPSE",
  "Fecha del registro REPSE",
  "Estatus del registro REPSE",
  "Actividad económica especializada manifestada en REPSE",
  "Folio del contrato reportado en ICSOE",
  "Folio ICSOE",
  "Fecha de presentación ICSOE",
];

export const ESPECIFICACIONES_TRABAJADORES = [
  "ESPECIFICACIONES PARA EL LLENADO",
  "**RP de la empresa de la empresa prestadora de servicios",
  "**De acuerdo al registrado por la empresa",
  "**De acuerdo al registrado por la empresa",
  "**De acuerdo al registrado por la empresa",
  "**De acuerdo al registrado por la empresa",
  "**Debe ser a 13 digitos",
  "**Debe ser a 18 digitos",
  "**Puesto de acuerdo lo maneja",
  "Opciones de respuesta: Operativo, Adminitrativo, Profesional",
  "**De acuerdo al registrado por la empresa",
  "**Formato de fecha DD/MM/AAAA",
  "**De acuerdo al registrado por la empresa",
  "**Es el folio que vienen en el acuse de Aviso de Registro REPSE Ejemplo: AR99990/2021",
  "**Formato de fecha DD/MM/AAAA",
  "**Opciones de respuesta: Registro en autorización, Registro vigente, Registro en actualización, Sin registro, Registro cancelado",
  "**Se encuentra registrada en REPSE en el Aviso de Registro REPSE",
  "** Ejemplo A21P0000001",
  "** Ejemplo N21P0000001 Nota: pueden ser tambien las declaraciones complementarias",
  "**Formato de fecha DD/MM/AAAA",
];

export const BANNERS_TRABAJADORES = [
  { fila: 0, columna: 2, texto: "Sección A. Datos del Trabajador" },
];

export const MERGES_BANNER_TRABAJADORES = [
  { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },   // A1:B1
  // En la plantilla original "Sección A" vive en C1 sin merge y su texto se
  // desborda sobre las celdas vacías de al lado. Aquí sí se merge hasta "Días
  // trabajados", que es donde termina la sección: se ve igual y no depende de
  // que las celdas de la derecha estén vacías.
  { s: { r: 0, c: 2 }, e: { r: 0, c: 12 } },
  { s: { r: 0, c: 13 }, e: { r: 0, c: 19 } }, // N1:T1
];

export const ANCHOS_TRABAJADORES = [
  12, 24, 18, 18, 24, 14, 16, 20, 22, 16, 12, 14, 16, 18, 16, 20, 30, 20, 18, 18,
];
